import { FC, useCallback, useEffect, useState } from "react";
import { ActivityType, IActivityEntry } from "@hulanbv/platformapp";
import { classes, stylesheet } from "typestyle";
import { Flex } from "../../../elements/flex.element";
import { Button } from "../../../elements/button.element";
import { Card } from "../../../elements/card.element";

type Props = {
  activityEntries: IActivityEntry[];
  onComplete: (definedObstacleEntries: IActivityEntry[]) => void;
};

const ValueObstacleEntryView: FC<Props> = ({ onComplete, activityEntries }) => {
  const [currentObstacle, setCurrentObstacle] = useState("");
  const [cardNumber, setCardNumber] = useState(0);
  const [definedObstacleEntries, setDefinedObstacleEntries] = useState<
    [identifiedValue: IActivityEntry, obstacle: string][]
  >([]);

  const [questionStack, setQuestionStack] =
    useState<IActivityEntry[]>(activityEntries);

  useEffect(() => {
    if (definedObstacleEntries.length >= activityEntries.length) {
      onComplete(
        definedObstacleEntries.map(
          ([identifiedValue, obstacle]) =>
            ({
              input: identifiedValue.value,
              value: obstacle,
              activityType: ActivityType.VALUE_OBSTACLES,
              userId: identifiedValue.userId,
              exerciseInviteId: identifiedValue.exerciseInviteId,
            } as IActivityEntry),
        ),
      );
    }
  }, [cardNumber, activityEntries, definedObstacleEntries, onComplete]);

  const submitEntry = useCallback(() => {
    if (currentObstacle.trim() === "") {
      return;
    }

    setDefinedObstacleEntries((prev) => [
      ...prev,
      [questionStack[cardNumber], currentObstacle.trim()],
    ]);
    setCurrentObstacle("");
    setCardNumber((prev) => prev + 1);
  }, [questionStack, cardNumber, currentObstacle]);

  const skipEntry = useCallback(() => {
    setCurrentObstacle("");
    setQuestionStack((prev) => questionStack.concat(prev[cardNumber]));
    setCardNumber((prev) => prev + 1);
  }, [cardNumber, questionStack]);

  return (
    <Flex direction="column" alignItems="stretch" flex={1}>
      <Flex direction="column" flex={0}>
        <p className={styles.info}>
          When you think about moving toward this value, list all the reasons
          your mind tells why you <strong>can't</strong>
        </p>
      </Flex>
      <Flex
        flex={1}
        direction="column"
        justifyContent="stretch"
        attributes={{ style: { position: "relative" } }}
      >
        {questionStack?.map((component, index) => {
          const position = index - cardNumber;
          let status: "kept" | "discarded" | "undecided" = "undecided";
          if (index < cardNumber) {
            status = definedObstacleEntries.some(
              ([identifiedValue]) => identifiedValue.id === component.id,
            )
              ? "kept"
              : "discarded";
          }
          return (
            <Card
              key={`${component.id}-${index}`}
              attributes={{
                className: classes(styles.card, styles[status]),
                style: {
                  zIndex: questionStack.length - position,
                  transform: `translateY(${position * 15}px) scale(${
                    1 - position * 0.05
                  })`,
                  opacity: position < 0 ? 0 : 1.33 - position * 0.33,
                  pointerEvents: index === cardNumber ? "auto" : "none",
                  transition:
                    "transform 0.5s, opacity 1s, translate 0.5s, rotate 0.5s",
                },
              }}
            >
              <Flex
                direction="column"
                alignItems="stretch"
                flex={1}
                padding={20}
              >
                <p style={{ margin: 0 }}>{component.value}</p>
                <textarea
                  className={styles.input}
                  style={{ flex: 1 }}
                  placeholder="List the obstacles here.."
                  onInput={(event) =>
                    setCurrentObstacle(
                      (event.target as HTMLTextAreaElement).value,
                    )
                  }
                />
                <Flex>
                  <Button
                    flavour="ghost-primary"
                    centerChildren
                    attributes={{
                      onClick: skipEntry,
                      style: {
                        visibility:
                          definedObstacleEntries.length + 1 >=
                          activityEntries.length
                            ? "hidden"
                            : "visible",
                      },
                    }}
                  >
                    Later
                  </Button>
                  <Button
                    hideSpinnerOnSubmit
                    centerChildren
                    attributes={{
                      onClick: submitEntry,
                      disabled: currentObstacle.trim() === "",
                    }}
                  >
                    Submit
                  </Button>
                </Flex>
              </Flex>
            </Card>
          );
        })}
      </Flex>
      <Flex flex={0}>
        <p className={styles.info}>
          {definedObstacleEntries.length} of {activityEntries?.length ?? 0}{" "}
          obstacles defined
        </p>
      </Flex>
    </Flex>
  );
};

export { ValueObstacleEntryView };

const styles = stylesheet({
  info: {
    fontSize: ".9em",
    color: "rgb(var(--rgb-color-text-tertiary))",
    textAlign: "center",
    margin: 0,
  },

  card: {
    height: "90%",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "stretch",
    position: "absolute",
    translate: "0 0",
  },

  input: {
    fontFamily: "inherit",
    position: "relative",
    outline: "none",
    fontSize: "0.93rem",
    color: "rgb(var(--rgb-color-black))",
    backgroundColor: "rgb(var(--rgb-color-gray-lighter))",
    padding: "19px 14px",
    border: "1px solid transparent",
    borderRadius: "var(--border-radius-large)",
    width: "100%",
    resize: "none",
    $nest: {
      "&:focus": {
        border: "1px solid rgb(var(--rgb-color-primair-basis))",
      },
      "&::placeholder": {
        color: "rgba(var(--rgb-color-black), 0.5)",
        fontSize: "0.93rem",
      },
    },
  },

  kept: {
    translate: "100% 0 !important",
    rotate: "20deg",
    transformOrigin: "right bottom",
  },

  discarded: {
    translate: "-100% 0 !important",
    rotate: "-20deg",
    transformOrigin: "left bottom",
  },

  undecided: {},
});
